import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';

import { AuthService } from '../../../../_core/api/auth.service';
import { CustomValidators } from '../../../../_core/helpers/custom-validators.helpers';
import { COUNTRIES } from '../../../../_core/constants/countries';
import { EnvironmentNames } from '../../../../_core/constants/environments';
import { CurrencySet } from '../../../../_core/models/contributeData';
import { Fund } from '../../../../_core/models/fund';
import { SearchCountry } from '../../../donate/donate-form/donate-form.model';
import { Configuration } from '../../../../app.constants';

@Component({
  selector: 'app-add-charity-form',
  templateUrl: './add-charity-form.component.html',
  styleUrls: ['./add-charity-form.component.scss'],
  standalone: false,
})
export class AddCharityFormComponent implements OnInit {
  @Input() fund: Fund;
  @Output() formChangedEvent = new EventEmitter<any>();
  @Output() formIsValid = new EventEmitter<boolean>();

  form: FormGroup;
  countries = COUNTRIES as unknown as SearchCountry[];
  availableCurrencies: CurrencySet[] = [];
  currencyDisabled = false;
  isCncf = false;
  isSinngeber = false;

  constructor(
    private fb: FormBuilder,
    private config: Configuration,
    private authService: AuthService
  ) {
    this.isCncf = this.config.environmentName === EnvironmentNames.CNCF;
    this.isSinngeber = this.config.environmentName === EnvironmentNames.SINNGEBER;
  }

  ngOnInit(): void {
    this.createForm();
    this.loadCurrencies();
    this.form.valueChanges.pipe(debounceTime(400)).subscribe(() => this.emitChanges());
  }

  private loadCurrencies(): void {
    this.authService.getCurrencies().subscribe((currencies: any[]) => {
      this.availableCurrencies = currencies.map((c: any) => ({ ...c, uuid: c.id }));

      if (this.isCncf) {
        const cad = this.availableCurrencies.find((c) => c.symbol === 'CAD');
        if (cad) {
          this.currencyDisabled = true;
          this.form.get('amountCurrency').patchValue({ amount: null, currency: cad });
        }
      } else if (this.isSinngeber) {
        const eur = this.availableCurrencies.find((c) => c.symbol === 'EUR');
        if (eur) {
          this.currencyDisabled = true;
          this.form.get('amountCurrency').patchValue({ amount: null, currency: eur });
        }
      }
    });
  }

  private createForm(): void {
    const registrationValidators = this.isCncf ? [Validators.required] : [];

    this.form = this.fb.group({
      charityName: [
        '',
        [
          Validators.required,
          CustomValidators.minLength(5),
          CustomValidators.maxLength(70),
          CustomValidators.lettersNumbersCharacters,
        ],
      ],
      registrationNumber: ['', registrationValidators],
      amountCurrency: [{ amount: null, currency: null }],
      country: [null, [Validators.required]],
      contactName: [
        '',
        [
          Validators.required,
          CustomValidators.minLength(5),
          CustomValidators.maxLength(50),
          CustomValidators.lettersNumbersCharacters,
        ],
      ],
      contactEmail: ['', [Validators.required, CustomValidators.email]],
      contactPhone: ['', [Validators.required, CustomValidators.phone]],
      charityAddress: [''],
      comments: ['', [CustomValidators.maxLength(250)]],
    });
  }

  private emitChanges(): void {
    const value = this.form.getRawValue();
    const { amountCurrency, country: rawCountry, ...rest } = value;
    const formValue = {
      ...rest,
      amount: amountCurrency?.amount,
      currency: amountCurrency?.currency,
      country: typeof rawCountry === 'object' && rawCountry !== null ? rawCountry.name : rawCountry,
    };
    this.formChangedEvent.emit(formValue);
    this.formIsValid.emit(this.form.valid);
  }
}
